import React from 'react';
import CircularExampleTile from '@blocks/featured-circular-example-block/circular-example-tile/circular-example-tile';
import LoadingTile from '../tile/loading-tile';
import Tile from '../tile/tile';
import { generateKey } from '../../../../common/utils/utils';
import {
  generateUrl,
  generateSubPageUrl,
} from '../../../../common/utils/generate-url-functions';
import { RuntimeTileProps } from './fetch-runtime-tile-data.types';
import { transformTags } from '../../../../common/utils/transform-data-functions';
import { useQuery, gql } from '@apollo/client';

const standardPageRunTimeQuery = gql`
  query StandardPageQuery($id: String!, $localeCode: String!) {
    standardPage(id: $id, locale: $localeCode) {
      sys {
        id
      }
      enabled
      slug
      pageType
      listingTitle
      listingDescription
      listingImage {
        url
        description
        contentType
      }
      mastheadImage {
        url
        description
        contentType
      }
      topics
      resourceContentType
    }
  }
`;

const circularExamplePageRunTimeQuery = gql`
  query CircularExamplePageQuery($id: String!, $localeCode: String!) {
    caseStudy(id: $id, locale: $localeCode) {
      sys {
        id
      }
      enabled
      title
      slug
      listingTitle
      listingDescription
      listingImage {
        url
        description
        contentType
      }
      topics
      resourceContentType
    }
  }
`;

const subPageRunTimeQuery = gql`
  query SubPageQuery($id: String!, $localeCode: String!) {
    subPage(id: $id, locale: $localeCode) {
      sys {
        id
      }
      enabled
      title
      slug
      listingTitle
      listingTitle
      listingImage {
        url
        description
        contentType
      }
      topics
      parentStructure {
        enabled
        type
        slug
      }
      resourceContentType
    }
  }
`;

const resourceRunTimeQuery = gql`
  query ResourceRunTimeQuery($id: String!, $localeCode: String!) {
    resource(id: $id, locale: $localeCode) {
      sys {
        id
      }
      enabled
      listingTitle: title
      type
      bynderFile
      url
      listingDescription: description
      listingImage: image {
        url
        description
        contentType
      }
      topics
      resourceContentType
    }
  }
`;

const webflowPageRunTimeQuery = gql`
  query WebflowPageQuery($id: String!, $localeCode: String!) {
    webflowPage(id: $id, locale: $localeCode) {
      sys {
        id
      }
      enabled
      slug
      pageType
      listingTitle
      listingDescription
      listingImage {
        url
        description
        contentType
      }
      topics
      resourceContentType
    }
  }
`;

const FetchRuntimeTileData: React.FC<RuntimeTileProps> = ({
  contentfulId,
  contentType,
  tileType,
  ceTile = false,
  className,
  isCompact = false,
  hasBlockTitle = false,
}) => {
  const id = contentfulId;
  const localeCode = 'en-GB';
  let query;
  let structureType = contentType;
  switch (contentType) {
    case 'standardPage':
      query = standardPageRunTimeQuery;
      break;
    case 'circularExamplePage':
      query = circularExamplePageRunTimeQuery;
      structureType = 'caseStudy';
      break;
    case 'subPage':
      query = subPageRunTimeQuery;
      break;
    case 'resource':
      query = resourceRunTimeQuery;
      break;
    case 'webflowPage':
      query = webflowPageRunTimeQuery;
      break;
    default:
      query = null;
      console.warn(
        'FetchRuntimeTileData: Unable to assign query. Unrecognised contentType received. contentType=',
        contentType
      );
  }

  const { loading, error, data } = useQuery(query, {
    variables: { id, localeCode },
  });

  if (loading) return <LoadingTile tileType={tileType} />;
  if (error) {
    console.error('Unable to load tile runtime data. ', error.message);
    return null;
  }

  const tileData = data[structureType];

  if (tileData && tileData.enabled === true) {
    const tileImage =
      tileData.listingImage ||
      tileData.mastheadImage ||
      tileData.mastHeadBackgroundImage;
    const tags = transformTags(tileData.topics);
    let tileUrl;
    let displayTile = true;
    switch (contentType) {
      case 'standardPage':
        tileUrl = generateUrl(localeCode, tileData.pageType, tileData.slug);
        break;
      case 'circularExamplePage':
        tileUrl = generateUrl(localeCode, 'circularExamplePage', tileData.slug);
        break;
      case 'subPage':
        displayTile = tileData.parentStructure
          ? tileData.parentStructure.enabled
          : false;
        if (displayTile) {
          tileUrl = generateSubPageUrl(
            localeCode,
            tileData.parentStructure.type,
            tileData.parentStructure.slug,
            tileData.slug
          );
        } else {
          tileUrl = false;
        }
        break;
      case 'webflowPage':
        tileUrl = generateUrl(localeCode, tileData.pageType, tileData.slug);
        break;
      case 'resource':
        tileUrl =
          tileData.bynderFile?.[0]?.original || tileData.url || undefined;
        break;
      default:
        tileUrl = null;
        console.warn(
          'FetchRuntimeTileData: Unable to assign tileUrl. Unrecognised contentType recieved. contentType=',
          contentType
        );
    }
    if (displayTile && tileUrl && !ceTile) {
      return (
        <Tile
          key={generateKey(tileData.listingTitle, id)}
          tileType={tileType}
          contentType={contentType}
          title={tileData.listingTitle}
          description={tileData.listingDescription}
          imageUrl={tileImage.url}
          imageAlt={tileImage.description}
          imageFormat={tileImage.contentType}
          tags={tags}
          tagLimit={3}
          url={tileUrl}
          locale={localeCode}
          localeUnavailable={true}
          translate={true}
          isCompact={isCompact}
          hasBlockTitle={hasBlockTitle}
          resourceContentType={tileData.resourceContentType}
        />
      );
    } else if (ceTile) {
      return (
        <CircularExampleTile
          className={className}
          data-testid="circular-example-tile"
          key={generateKey(tileData.listingTitle, id)}
          tileType={tileType}
          title={tileData.listingTitle}
          imageUrl={tileImage.url}
          imageAlt={tileImage.description}
          imageFormat={tileImage.contentType}
          url={tileUrl}
          localeCode={localeCode}
          translate={true}
          localeUnavailable={true}
        />
      );
    } else {
      console.warn(
        'FetchRuntimeTileData: Unable to return Tile component. Check failed on displayTile=',
        displayTile,
        ' && tileUrl=',
        tileUrl
      );
      return null;
    }
  }
  return null;
};

export default FetchRuntimeTileData;
